/**
 * DC stock auto-filter bootstrap.
 *
 * Reads the embedded `<script type="application/json" data-dc-stock-values>`
 * block rendered by main-collection / amb-main-collection / search-results
 * when the DC stock auto-filter is enabled, then:
 *   1. On first load — if the URL is missing one or more expected
 *      `paramName=value` entries, hard-redirects via `location.replace` so
 *      Shopify's S&D returns a paginated set that already excludes
 *      `stock_product_*_dc = 0` products.
 *   2. Exposes `window.DcStockFilter` so `facets.js` (form submits, sort,
 *      removeFilter, etc.) and any other code can re-inject the DC params on
 *      every URL it builds.
 *   3. Intercepts "Clear all" / individual remove links so the DC params are
 *      preserved when the user clears other filters.
 *
 * Side-effects only — loaded as a regular script from `layout/theme.liquid`
 * on collection/search templates (see plan `dc_stock_auto-filter_plan_8075e76e`).
 */
(function () {
  if (window.DcStockFilter) return;

  var DATA_LOADING = 'data-dc-loading';
  var DATA_BOOTSTRAPPED = 'data-dc-bootstrapped';
  var SELECTOR_RESULTS_LIST = 'results-list[data-dc-pagination="true"]';
  var SELECTOR_VALUES_JSON = 'script[data-dc-stock-values]';
  var CLEAR_SELECTORS = [
    'a.active-facets__button-remove',
    'a.mobile-facets__clear',
    'facet-remove-component[data-url]',
  ];

  var state = {
    paramName: '',
    /** @type {string} PLP scope (collection handle, "search", or "") */
    scope: '',
    values: [],
    initialized: false,
  };

  /**
   * Best-effort scope inference for legacy/missing `data-collection-scope`.
   * Tolerates locale prefixes like `/en-au/collections/foo` or `/fr/search`.
   *
   * @returns {string}
   */
  function inferScopeFromPath() {
    try {
      var path = window.location.pathname;
      if (/(?:^|\/)search(?:\/|$)/i.test(path)) return 'search';
      var m = path.match(/(?:^|\/)collections\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (err) {
      return '';
    }
  }

  function readEmbeddedConfig() {
    var node = document.querySelector(SELECTOR_VALUES_JSON);
    if (!node) return false;

    var paramName = node.getAttribute('data-param-name') || '';
    if (!paramName) return false;

    var attrScope = (node.getAttribute('data-collection-scope') || '').trim();
    var scope = attrScope || inferScopeFromPath();

    var values = [];
    try {
      var parsed = JSON.parse(node.textContent || '[]');
      if (Array.isArray(parsed)) {
        for (var i = 0; i < parsed.length; i++) {
          var raw = parsed[i];
          if (raw === null || raw === undefined) continue;
          // Defensive split: tolerate comma-joined entries in the JSON
          // (e.g. ["1,2,3"]) so they expand into ["1","2","3"].
          var subParts = String(raw).split(',');
          for (var k = 0; k < subParts.length; k++) {
            var s = subParts[k].trim();
            if (s === '' || s === '0') continue;
            if (values.indexOf(s) === -1) values.push(s);
          }
        }
      }
    } catch (err) {
      console.warn('[dc-stock-filter] failed to parse values JSON', err);
      return false;
    }

    // In-memory union for the SAME PLP scope only.
    // `filter.values` can shrink once the URL applies the DC param (Shopify
    // returns only the actively-filtered values), so we keep what we saw on
    // the first render this session. We do NOT persist across navigation —
    // localStorage caused stale value drift when metafield options change.
    // On scope change (PLP switch) the union resets so we never leak values
    // from another collection / search session.
    var union = values.slice();
    if (state.scope === scope) {
      for (var p = 0; p < state.values.length; p++) {
        if (union.indexOf(state.values[p]) === -1) union.push(state.values[p]);
      }
    }

    state.paramName = paramName;
    state.scope = scope;
    state.values = union;
    return true;
  }

  /**
   * Mutates a URLSearchParams instance so the configured `paramName` carries
   * every non-zero DC value as a single comma-joined entry — matching
   * Shopify's storefront filtering URL format
   * (`filter.p.m.custom.stock_product_au_dc=1,2,3`).
   *
   * Tolerates URLs that arrive in either supported format (comma list OR
   * repeated keys) and rewrites them to the canonical comma list. `0` values
   * are stripped because the whole point is to hide OOS-by-DC products.
   *
   * @param {URLSearchParams} params
   * @returns {URLSearchParams}
   */
  /**
   * Search S&D only exposes product metafield filters when type=product is
   * present. Applied in dc-stock-filter.js only (search scope), not flyout nav.
   *
   * @param {URLSearchParams} params
   */
  function ensureSearchProductType(params) {
    if (state.scope !== 'search') return params;
    if (!params.get('q')) return params;
    params.set('type', 'product');
    return params;
  }

  function injectInto(params) {
    if (!state.paramName || !state.values.length) return params;

    ensureSearchProductType(params);
    var merged = {};
    var existing = params.getAll(state.paramName);
    for (var i = 0; i < existing.length; i++) {
      var parts = String(existing[i]).split(',');
      for (var k = 0; k < parts.length; k++) {
        var p = parts[k].trim();
        if (p !== '' && p !== '0') merged[p] = true;
      }
    }
    for (var j = 0; j < state.values.length; j++) {
      merged[state.values[j]] = true;
    }

    // Stable order: configured values first (so the URL stays predictable
    // across renders), then any extras the URL contributed that we don't
    // know about — keeps user-pinned values intact.
    var ordered = [];
    for (var v = 0; v < state.values.length; v++) {
      if (merged[state.values[v]]) {
        ordered.push(state.values[v]);
        delete merged[state.values[v]];
      }
    }
    for (var extra in merged) {
      if (Object.prototype.hasOwnProperty.call(merged, extra)) ordered.push(extra);
    }

    params.delete(state.paramName);
    if (ordered.length) {
      params.set(state.paramName, ordered.join(','));
    }
    return params;
  }

  /**
   * Decodes the comma separator (`%2C` -> `,`) that URLSearchParams /
   * URL.search insists on encoding. Shopify's storefront filter parser
   * splits on literal commas only — `?filter.p.m.custom.x=1%2C2` is
   * interpreted as the single value `"1,2"` and matches nothing.
   *
   * @param {string} search - A URL search string (with or without leading `?`).
   * @returns {string}
   */
  function normalizeSearch(search) {
    if (!search) return '';
    return search.replace(/%2C/gi, ',');
  }

  /**
   * Returns a new URL string that includes the DC params. Accepts a string,
   * URL, or relative path. Safe to call with empty / hash-only inputs.
   *
   * @param {string | URL} url
   * @returns {string}
   */
  function appendTo(url) {
    if (!state.paramName || !state.values.length) return String(url || '');

    var inputStr = String(url || '');
    if (!inputStr) inputStr = window.location.href;

    var resolved;
    try {
      resolved = new URL(inputStr, window.location.origin);
    } catch (err) {
      return inputStr;
    }

    injectInto(resolved.searchParams);
    ensureSearchProductType(resolved.searchParams);
    return resolved.pathname + normalizeSearch(resolved.search) + (resolved.hash || '');
  }

  /**
   * Removes the DC stock filter entries from a FormData (used so the visible
   * form never accidentally serializes DC values that the user can't see).
   *
   * @param {FormData} formData
   */
  function stripFromForm(formData) {
    if (!state.paramName || !formData) return;
    if (typeof formData.delete === 'function') {
      formData.delete(state.paramName);
    }
  }

  /**
   * @returns {boolean} true when this page renders the auto-filter and we
   *   have at least one non-zero value to inject.
   */
  function isActive() {
    return Boolean(state.paramName) && state.values.length > 0 && Boolean(document.querySelector(SELECTOR_RESULTS_LIST));
  }

  function setLoadingState(on) {
    var node = document.querySelector(SELECTOR_RESULTS_LIST);
    if (!node) return;
    if (on) {
      node.setAttribute(DATA_LOADING, 'true');
      node.setAttribute('aria-busy', 'true');
    } else {
      node.removeAttribute(DATA_LOADING);
      node.removeAttribute('aria-busy');
      node.setAttribute(DATA_BOOTSTRAPPED, 'true');
    }
  }

  function urlMissingDcValues() {
    var current = new URL(window.location.href);
    var existing = current.searchParams.getAll(state.paramName);
    if (!existing.length) return true;
    // Flatten any combination of repeated keys + comma-joined entries so we
    // never redirect a URL that already has every value (in any format).
    var seen = {};
    for (var i = 0; i < existing.length; i++) {
      var parts = String(existing[i]).split(',');
      for (var k = 0; k < parts.length; k++) {
        var p = parts[k].trim();
        if (p) seen[p] = true;
      }
    }
    for (var j = 0; j < state.values.length; j++) {
      if (!seen[state.values[j]]) return true;
    }
    return false;
  }

  function maybeRedirectSearchProductType() {
    try {
      if (!/(?:^|\/)search(?:\/|$)/i.test(window.location.pathname)) return false;
      var url = new URL(window.location.href);
      if (!url.searchParams.get('q')) return false;
      if (url.searchParams.get('type') === 'product') return false;
      url.searchParams.set('type', 'product');
      window.location.replace(url.pathname + normalizeSearch(url.search) + (url.hash || ''));
      return true;
    } catch (err) {
      return false;
    }
  }

  function maybeRedirect() {
    if (!isActive()) return false;
    if (!urlMissingDcValues()) return false;

    setLoadingState(true);
    var nextUrl = appendTo(window.location.href);
    window.location.replace(nextUrl);
    return true;
  }

  /**
   * Rewrites href / data-url on clear-all & remove-all anchors so they
   * preserve the DC stock params after navigation.
   */
  function rewriteClearLinks(root) {
    if (!isActive()) return;
    var scope = root || document;

    for (var i = 0; i < CLEAR_SELECTORS.length; i++) {
      var nodes = scope.querySelectorAll(CLEAR_SELECTORS[i]);
      for (var j = 0; j < nodes.length; j++) {
        var el = nodes[j];
        var href = el.getAttribute('href');
        if (href != null && href !== '') {
          el.setAttribute('href', appendTo(href));
        }
        var dataUrl = el.getAttribute('data-url');
        if (dataUrl != null && dataUrl !== '') {
          el.setAttribute('data-url', appendTo(dataUrl));
        }
      }
    }
  }

  function onDocumentClick(event) {
    if (!isActive()) return;
    var target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    for (var i = 0; i < CLEAR_SELECTORS.length; i++) {
      var match = target.closest(CLEAR_SELECTORS[i]);
      if (!match) continue;

      var hrefAttr = match.getAttribute('href');
      if (hrefAttr != null && hrefAttr !== '') {
        match.setAttribute('href', appendTo(hrefAttr));
      }
      var dataUrlAttr = match.getAttribute('data-url');
      if (dataUrlAttr != null && dataUrlAttr !== '') {
        match.setAttribute('data-url', appendTo(dataUrlAttr));
      }
      return;
    }
  }

  function init() {
    if (state.initialized) return;
    state.initialized = true;

    if (maybeRedirectSearchProductType()) return;

    var okEmbed = readEmbeddedConfig();
    var active = Boolean(okEmbed && isActive());

    // Keep overlay only across the hard redirect bootstrap; strip it whenever
    // we stay on this URL (missing JSON, zero-result / empty value list where
    // Liquid still pre-painted `data-dc-loading`).
    if (active && maybeRedirect()) return;

    setLoadingState(false);

    if (!active) return;

    rewriteClearLinks(document);

    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      onDocumentClick(event);
    }, true);

    // Re-read embedded config + rewrite clear-all hrefs whenever the
    // section is re-mounted (theme editor or section refresh). Skipped on
    // the regular `filter:update` lifecycle because the document-level
    // capture click handler already rewrites freshly-rendered links at
    // click time, and we deliberately want zero overlay churn after the
    // initial redirect.
    document.addEventListener('shopify:section:load', function () {
      readEmbeddedConfig();
      if (!isActive()) {
        setLoadingState(false);
      }
      rewriteClearLinks(document);
    });
  }

  window.DcStockFilter = {
    get paramName() {
      return state.paramName;
    },
    get values() {
      return state.values.slice();
    },
    isActive: isActive,
    appendTo: appendTo,
    injectInto: injectInto,
    stripFromForm: stripFromForm,
    rewriteClearLinks: rewriteClearLinks,
    normalizeSearch: normalizeSearch,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
